<template>
  <div class="ccma-recruitment-search">
    <h4>{{ t('consultantCorner.RecruitmentSearch') }}</h4>
    <p class="section-description">{{ t('consultantCorner.RecruitmentSearchDescription') }}</p>
    
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>{{ t('consultantCorner.LoadingVisibilitySettings') }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="hasError" class="error-container">
      <div class="error-message">
        <h5>{{ t('consultantCorner.LoadError') }}</h5>
        <p>{{ errorMessage }}</p>
        <button @click="loadVisibilitySettings" class="btn-simple">
          {{ t('consultantCorner.TryAgain') }}
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="visibility-options">
      <h5>{{ t('consultantCorner.VisibilitySettings') }}</h5>
      
      <div class="checkbox-list">
        <!-- Fixed checkboxes -->
        <div class="checkbox-container">
          <label class="checkbox-wrapper">
            <input 
              v-model="formData.showName" 
              type="checkbox" 
              class="checkbox-input"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">
              {{ t('consultantCorner.ShowName') }}
            </span>
          </label>
        </div>

        <div class="checkbox-container">
          <label class="checkbox-wrapper">
            <input 
              v-model="formData.showLocation" 
              type="checkbox" 
              class="checkbox-input"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">
              {{ t('consultantCorner.ShowLocation') }}
            </span>
          </label>
        </div>

        <div class="checkbox-container">
          <label class="checkbox-wrapper">
            <input 
              v-model="formData.showPhoto" 
              type="checkbox" 
              class="checkbox-input"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">
              {{ t('consultantCorner.ShowPhoto') }}
            </span>
          </label>
        </div>

        <div class="checkbox-container">
          <label class="checkbox-wrapper">
            <input 
              v-model="formData.showEmail" 
              type="checkbox" 
              class="checkbox-input"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">
              {{ t('consultantCorner.ShowEmail') }}
            </span>
          </label>
        </div>

        <div class="checkbox-container">
          <label class="checkbox-wrapper">
            <input 
              v-model="formData.showLandline" 
              type="checkbox" 
              class="checkbox-input"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">
              {{ t('consultantCorner.ShowLandline') }}
            </span>
          </label>
        </div>

        <div class="checkbox-container">
          <label class="checkbox-wrapper">
            <input 
              v-model="formData.showMobile" 
              type="checkbox" 
              class="checkbox-input"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">
              {{ t('consultantCorner.ShowMobile') }}
            </span>
          </label>
        </div>
      </div>

      <div class="form-actions">
        <button 
          @click="updateSettings" 
          class="btn-update"
          :disabled="isSaving"
        >
          <span v-if="isSaving">{{ t('consultantCorner.companyName.Saving') }}</span>
          <span v-else>{{ t('consultantCorner.companyName.UpdateOptOut') }}</span>
        </button>
      </div>
    </div>

    <!-- Success/Error Notifications -->
    <div v-if="showNotification" class="notification" :class="notificationType">
      {{ notificationMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { 
  UserVisibilitySettingsGet, 
  UserVisibilitySettingsUpdate 
} from '../../api/Order'
import type { 
  ISfaUserSettingViewModel,
  IUserVisibilitySettingsUpdateRequest 
} from '../../types/OrderTypes'

const { t } = useI18n()
const logger = useLogger()

// Reactive state
const isLoading = ref(true)
const hasError = ref(false)
const errorMessage = ref('')
const isSaving = ref(false)
const showNotification = ref(false)
const notificationType = ref<'success' | 'error'>('success')
const notificationMessage = ref('')

// Data
const formData = ref({
  showName: true,
  showLocation: true,
  showPhoto: true,
  showEmail: true,
  showLandline: true,
  showMobile: true
})

// Methods
const showNotificationMessage = (message: string, type: 'success' | 'error' = 'success') => {
  notificationMessage.value = message
  notificationType.value = type
  showNotification.value = true
  
  setTimeout(() => {
    showNotification.value = false
  }, 5000)
}

const loadVisibilitySettings = async () => {
  try {
    isLoading.value = true
    hasError.value = false
    
    // Load current settings
    const settingsResponse = await UserVisibilitySettingsGet()
    if (settingsResponse.items) {
      // Apply existing settings
      settingsResponse.items.forEach(setting => {
        const isChecked = setting.settingValue === 'true'
        
        if (setting.settingName === 'ShowName') {
          formData.value.showName = isChecked
        } else if (setting.settingName === 'ShowLocation') {
          formData.value.showLocation = isChecked
        } else if (setting.settingName === 'ShowPhoto') {
          formData.value.showPhoto = isChecked
        } else if (setting.settingName === 'ShowEmail') {
          formData.value.showEmail = isChecked
        } else if (setting.settingName === 'ShowLandline') {
          formData.value.showLandline = isChecked
        } else if (setting.settingName === 'ShowMobile') {
          formData.value.showMobile = isChecked
        }
      })
    }
    
  } catch (error) {
    hasError.value = true
    errorMessage.value = error instanceof Error ? error.message : t('consultantCorner.LoadError')
    logger.error('Error loading visibility settings:', error)
  } finally {
    isLoading.value = false
  }
}

const updateSettings = async () => {
  try {
    isSaving.value = true
    
    // Prepare settings array
    const settings: ISfaUserSettingViewModel[] = [
      {
        settingName: 'ShowName',
        settingValue: formData.value.showName.toString(),
        settingType: 1
      },
      {
        settingName: 'ShowLocation',
        settingValue: formData.value.showLocation.toString(),
        settingType: 1
      },
      {
        settingName: 'ShowPhoto',
        settingValue: formData.value.showPhoto.toString(),
        settingType: 1
      },
      {
        settingName: 'ShowEmail',
        settingValue: formData.value.showEmail.toString(),
        settingType: 1
      },
      {
        settingName: 'ShowLandline',
        settingValue: formData.value.showLandline.toString(),
        settingType: 1
      },
      {
        settingName: 'ShowMobile',
        settingValue: formData.value.showMobile.toString(),
        settingType: 1
      }
    ]
    
    // Prepare request
    const request: IUserVisibilitySettingsUpdateRequest = {
      genPlanGuid: '0',
      beeNumber: 0,
      settings
    }
    
    const response = await UserVisibilitySettingsUpdate(request)
    
    if (response.responseStatus && response.responseStatus.errorCode) {
      throw new Error(response.responseStatus.message || 'Error updating visibility settings')
    }
    
    showNotificationMessage('Visibility settings updated successfully', 'success')
    logger.info('Visibility settings updated successfully')
    
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Error updating visibility settings'
    showNotificationMessage(message, 'error')
    logger.error('Error updating visibility settings:', error)
  } finally {
    isSaving.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadVisibilitySettings()
})
</script>

<style lang="scss" scoped>
.ccma-recruitment-search {
  padding: $card-padding;
}

.section-description {
  color: #666;
  margin-bottom: 2rem;
  font-style: italic;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #e67e22;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-container {
  display: flex;
  justify-content: center;
  padding: 2rem;
}

.error-message {
  text-align: center;
  padding: 2rem;
  border: 1px solid #dc3545;
  border-radius: $radius-md;
  background-color: #f8d7da;
  color: #721c24;
  max-width: 500px;
}

.error-message h5 {
  color: #721c24;
  margin-bottom: 1rem;
}

/* Simple button styles */
.btn-simple {
  padding: $spacing-2 $spacing-4;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  @include typography-small;
  border-radius: $radius-sm;
  cursor: pointer;
}

.btn-simple:hover {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
}

.btn-simple:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Main Content */
.visibility-options h5 {
  @include typography-body-large;
  color: #333;
  margin-bottom: 1rem;
}

/* Checkbox styles - same as CCMACompanyName.vue */
.checkbox-list {
  margin: 0 0 2rem 0;
}

.checkbox-container {
  margin-bottom: 0.75rem;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  @include typography-body;
  @include font-weight-medium;
  gap: 0.75rem;
  cursor: pointer;
  color: #333;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkbox-custom {
  position: relative;
  width: 20px;
  height: 20px;
  border: 2px solid #ced4da;
  border-radius: $radius-sm;
  background-color: var(--vt-c-white);
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.checkbox-input:checked + .checkbox-custom {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.checkbox-input:checked + .checkbox-custom::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-wrapper:hover .checkbox-custom {
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.checkbox-input:focus + .checkbox-custom {
  box-shadow: 0 0 0 0.2rem rgba(var(--vt-c-teal-dark), 0.3);
}

.checkbox-text {
  user-select: none;
}

/* Form actions */
.form-actions {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.btn-update {
  padding: $spacing-2-5 $spacing-5;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  @include typography-body;
  @include font-weight-medium;
  border-radius: $radius-sm;
  cursor: pointer;
}

.btn-update:hover {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
}

.btn-update:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #6c757d;
}

/* Notifications */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  border-radius: $radius-sm;
  @include font-weight-medium;
  color: white;
  z-index: $z-notification;
  animation: slideIn 0.3s ease-out;
}

.notification.success {
  background-color: #28a745;
}

.notification.error {
  background-color: #dc3545;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 767px) {
  .ccma-recruitment-search {
    padding: $spacing-2-5;
  }
  
  .form-actions {
    text-align: center;
  }
  
  .btn-update {
    width: 100%;
  }
  
  .notification {
    top: 10px;
    right: 10px;
    left: 10px;
    width: auto;
  }
}
</style>
